/**
 * WhatsAppPage – WhatsApp Business Integration
 * QR-Code Verbindung, Konversationen, Einstellungen
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  MessageCircle, QrCode, Settings, BarChart3, Send,
  Phone, RefreshCw, LogOut, Wifi, WifiOff, User,
  Bot, Clock, Trash2, Search, ChevronLeft
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function WhatsAppPage() {
  const [activeTab, setActiveTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }, []);

  // Status polling
  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/whatsapp/status`, { headers: headers() });
      if (r.ok) {
        const data = await r.json();
        setStatus(data);
        // QR automatisch laden wenn pending
        if (data.connection === 'qr_pending' || data.qr_available) {
          fetchQR();
        }
      }
    } catch (e) { /* silent */ }
  }, [headers]);

  const fetchQR = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/whatsapp/qr`, { headers: headers() });
      if (r.ok) setQrData(await r.json());
    } catch (e) { /* silent */ }
  }, [headers]);

  const fetchConversations = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/whatsapp/conversations`, { headers: headers() });
      if (r.ok) setConversations(await r.json());
    } catch (e) { /* silent */ }
  }, [headers]);

  const fetchMessages = useCallback(async (phone) => {
    try {
      const r = await fetch(`${API}/api/whatsapp/conversations/${encodeURIComponent(phone)}`, { headers: headers() });
      if (r.ok) {
        setMessages(await r.json());
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (e) { /* silent */ }
  }, [headers]);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/whatsapp/settings`, { headers: headers() });
      if (r.ok) setSettings(await r.json());
    } catch (e) { /* silent */ }
  }, [headers]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/whatsapp/stats`, { headers: headers() });
      if (r.ok) setStats(await r.json());
    } catch (e) { /* silent */ }
  }, [headers]);

  // Initial load
  useEffect(() => {
    fetchStatus();
    fetchConversations();
    fetchSettings();
    fetchStats();
  }, [fetchStatus, fetchConversations, fetchSettings, fetchStats]);

  // Polling
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchStatus();
      if (activeTab === 'chat') {
        fetchConversations();
        if (selectedPhone) fetchMessages(selectedPhone);
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus, fetchConversations, fetchMessages, activeTab, selectedPhone]);

  // Actions
  const handleRestart = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/api/whatsapp/restart`, { method: 'POST', headers: headers() });
      toast.success('WhatsApp wird neu gestartet...');
      setTimeout(fetchStatus, 3000);
    } catch (e) {
      toast.error('Fehler beim Neustart');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    if (!window.confirm('WhatsApp wirklich abmelden? Sie müssen den QR-Code erneut scannen.')) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/whatsapp/logout`, { method: 'POST', headers: headers() });
      toast.success('WhatsApp abgemeldet');
      setStatus(null);
      setQrData(null);
      setTimeout(fetchStatus, 3000);
    } catch (e) {
      toast.error('Fehler beim Abmelden');
    }
    setLoading(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPhone) return;
    try {
      const r = await fetch(`${API}/api/whatsapp/send`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ to: selectedPhone, text: newMessage }),
      });
      if (r.ok) {
        setNewMessage('');
        await fetchMessages(selectedPhone);
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Senden fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Senden fehlgeschlagen');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const r = await fetch(`${API}/api/whatsapp/settings`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(settings),
      });
      if (r.ok) toast.success('Einstellungen gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteConversation = async (phone) => {
    if (!window.confirm(`Konversation mit ${phone} wirklich löschen?`)) return;
    try {
      await fetch(`${API}/api/whatsapp/conversations/${encodeURIComponent(phone)}`, {
        method: 'DELETE', headers: headers(),
      });
      setConversations(prev => prev.filter(c => c.phone !== phone));
      if (selectedPhone === phone) {
        setSelectedPhone(null);
        setMessages([]);
      }
      toast.success('Konversation gelöscht');
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  const isConnected = status?.connection === 'connected';

  const filteredConversations = conversations.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (c.phone || '').includes(q) ||
      (c.push_name || '').toLowerCase().includes(q) ||
      (c.klient_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-green-600" />
            WhatsApp Business
          </h1>
          <p className="text-sm text-slate-500 mt-1">KI-gestützte WhatsApp-Kommunikation</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            data-testid="whatsapp-connection-status"
            variant={isConnected ? 'default' : 'destructive'}
            className={isConnected ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {isConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
            {isConnected ? `Verbunden (${status?.phone || ''})` : (status?.connection || 'Getrennt')}
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4" data-testid="whatsapp-tabs">
          <TabsTrigger value="status" data-testid="whatsapp-tab-status">
            <QrCode className="w-4 h-4 mr-1" /> Verbindung
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="whatsapp-tab-chat">
            <MessageCircle className="w-4 h-4 mr-1" /> Konversationen
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="whatsapp-tab-settings">
            <Settings className="w-4 h-4 mr-1" /> Einstellungen
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="whatsapp-tab-stats">
            <BarChart3 className="w-4 h-4 mr-1" /> Statistiken
          </TabsTrigger>
        </TabsList>

        {/* === VERBINDUNG TAB === */}
        <TabsContent value="status">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <QrCode className="w-5 h-5" /> QR-Code
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                {isConnected ? (
                  <div className="text-center py-8" data-testid="whatsapp-connected-info">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <Wifi className="w-10 h-10 text-green-600" />
                    </div>
                    <p className="text-lg font-semibold text-green-700">WhatsApp verbunden</p>
                    <p className="text-sm text-slate-500 mt-1">Nummer: {status?.phone}</p>
                    <p className="text-xs text-slate-400 mt-1">{status?.messages_received || 0} Nachrichten empfangen</p>
                  </div>
                ) : qrData?.qr ? (
                  <div className="text-center" data-testid="whatsapp-qr-display">
                    <img src={qrData.qr} alt="WhatsApp QR-Code" className="w-64 h-64 mx-auto rounded-lg border" />
                    <p className="text-sm text-slate-600 mt-3">Scannen Sie diesen QR-Code mit WhatsApp</p>
                    <p className="text-xs text-slate-400 mt-1">WhatsApp &rarr; Einstellungen &rarr; Verknüpfte Geräte</p>
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="whatsapp-no-qr">
                    <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <WifiOff className="w-10 h-10 text-slate-400" />
                    </div>
                    <p className="text-slate-600">WhatsApp nicht verbunden</p>
                    <p className="text-sm text-slate-400 mt-1">
                      {status?.last_error || 'Starten Sie die Verbindung um einen QR-Code zu erhalten'}
                    </p>
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={handleRestart} disabled={loading} data-testid="whatsapp-restart-btn">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Neu verbinden
                  </Button>
                  {isConnected && (
                    <Button variant="destructive" size="sm" onClick={handleLogout} disabled={loading} data-testid="whatsapp-logout-btn">
                      <LogOut className="w-4 h-4 mr-1" /> Abmelden
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={fetchQR} data-testid="whatsapp-refresh-qr-btn">
                    <RefreshCw className="w-4 h-4 mr-1" /> QR aktualisieren
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Verbindungsstatus</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-slate-500">Status</p>
                    <p className="font-semibold capitalize">{status?.connection || 'Unbekannt'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-slate-500">Telefonnummer</p>
                    <p className="font-semibold">{status?.phone || '-'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-slate-500">Nachrichten empfangen</p>
                    <p className="font-semibold">{status?.messages_received || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-slate-500">Letzter Fehler</p>
                    <p className="font-semibold text-xs">{status?.last_error || 'Kein Fehler'}</p>
                  </div>
                </div>
                <Separator />
                <div className="text-xs text-slate-400 space-y-1">
                  <p>Der WhatsApp-Service läuft als separater Docker-Container.</p>
                  <p>Nach dem Scannen des QR-Codes bleibt die Verbindung bestehen.</p>
                  <p>Bei Problemen: "Neu verbinden" oder im Docker-Log nachsehen.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === KONVERSATIONEN TAB === */}
        <TabsContent value="chat">
          <div className="grid gap-4 md:grid-cols-3" style={{ height: 'calc(100vh - 280px)' }}>
            {/* Konversationsliste */}
            <Card className="md:col-span-1">
              <CardHeader className="pb-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="whatsapp-search-input"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-380px)]">
                  {filteredConversations.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">
                      Noch keine Konversationen
                    </div>
                  ) : (
                    filteredConversations.map(conv => (
                      <div
                        key={conv.phone}
                        data-testid={`whatsapp-conversation-${conv.phone}`}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50 ${
                          selectedPhone === conv.phone ? 'bg-green-50 border-l-2 border-l-green-500' : ''
                        }`}
                        onClick={() => {
                          setSelectedPhone(conv.phone);
                          fetchMessages(conv.phone);
                        }}
                      >
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-green-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-sm truncate">
                              {conv.klient_name || conv.push_name || conv.phone}
                            </p>
                            {conv.unread > 0 && (
                              <Badge className="bg-green-600 text-white text-xs ml-1">{conv.unread}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-slate-400">
                              <Phone className="w-3 h-3 inline mr-0.5" />{conv.phone}
                            </span>
                            {conv.klient_id && (
                              <Badge variant="outline" className="text-[10px] py-0">Klient</Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost" size="icon" className="flex-shrink-0 w-7 h-7 text-slate-400 hover:text-red-500"
                          onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv.phone); }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Nachrichtenverlauf */}
            <Card className="md:col-span-2 flex flex-col">
              {selectedPhone ? (
                <>
                  <CardHeader className="pb-2 border-b flex-row items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSelectedPhone(null)}>
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-green-700" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">
                          {conversations.find(c => c.phone === selectedPhone)?.klient_name ||
                            conversations.find(c => c.phone === selectedPhone)?.push_name ||
                            selectedPhone}
                        </p>
                        <p className="text-xs text-slate-400">{selectedPhone}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 p-0 flex flex-col">
                    <ScrollArea className="flex-1 p-4" style={{ height: 'calc(100vh - 460px)' }}>
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          data-testid={`whatsapp-msg-${msg.id}`}
                          className={`flex mb-3 ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[75%] rounded-xl px-4 py-2 ${
                            msg.direction === 'outgoing'
                              ? 'bg-green-600 text-white rounded-br-sm'
                              : 'bg-slate-100 text-slate-900 rounded-bl-sm'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                            <div className={`flex items-center gap-1 mt-1 text-[10px] ${
                              msg.direction === 'outgoing' ? 'text-green-200' : 'text-slate-400'
                            }`}>
                              {msg.is_ai && <Bot className="w-3 h-3" />}
                              <Clock className="w-3 h-3" />
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('de-DE', {
                                hour: '2-digit', minute: '2-digit'
                              }) : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </ScrollArea>
                    <form onSubmit={handleSendMessage} className="p-3 border-t flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Nachricht schreiben..."
                        disabled={!isConnected}
                        data-testid="whatsapp-message-input"
                        className="flex-1"
                      />
                      <Button type="submit" disabled={!isConnected || !newMessage.trim()} data-testid="whatsapp-send-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  </CardContent>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Konversation auswählen</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* === EINSTELLUNGEN TAB === */}
        <TabsContent value="settings">
          {settings && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">KI-Einstellungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">KI-Assistent aktiviert</p>
                    <p className="text-sm text-slate-500">Claude beantwortet eingehende Nachrichten automatisch</p>
                  </div>
                  <Switch
                    checked={settings.ai_enabled}
                    onCheckedChange={(v) => setSettings(s => ({ ...s, ai_enabled: v }))}
                    data-testid="whatsapp-ai-toggle"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Auto-Antwort</p>
                    <p className="text-sm text-slate-500">Automatisch auf jede Nachricht antworten</p>
                  </div>
                  <Switch
                    checked={settings.auto_reply}
                    onCheckedChange={(v) => setSettings(s => ({ ...s, auto_reply: v }))}
                    data-testid="whatsapp-auto-reply-toggle"
                  />
                </div>
                <Separator />
                <div>
                  <label className="font-medium block mb-1">Begrüßungsnachricht</label>
                  <textarea
                    className="w-full rounded-lg border border-slate-200 p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={settings.greeting_message || ''}
                    onChange={(e) => setSettings(s => ({ ...s, greeting_message: e.target.value }))}
                    data-testid="whatsapp-greeting-input"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="font-medium block mb-1">Bürozeiten von</label>
                    <Input
                      type="time" value={settings.office_hours_start || '08:00'}
                      onChange={(e) => setSettings(s => ({ ...s, office_hours_start: e.target.value }))}
                      data-testid="whatsapp-office-start"
                    />
                  </div>
                  <div>
                    <label className="font-medium block mb-1">Bürozeiten bis</label>
                    <Input
                      type="time" value={settings.office_hours_end || '18:00'}
                      onChange={(e) => setSettings(s => ({ ...s, office_hours_end: e.target.value }))}
                      data-testid="whatsapp-office-end"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-medium block mb-1">Abwesenheitsnachricht</label>
                  <textarea
                    className="w-full rounded-lg border border-slate-200 p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={settings.out_of_office_message || ''}
                    onChange={(e) => setSettings(s => ({ ...s, out_of_office_message: e.target.value }))}
                    data-testid="whatsapp-ooo-input"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Nur autorisierte Nummern</p>
                    <p className="text-sm text-slate-500">Nur bekannte Klienten-Nummern erhalten KI-Antworten</p>
                  </div>
                  <Switch
                    checked={settings.authorized_numbers_only}
                    onCheckedChange={(v) => setSettings(s => ({ ...s, authorized_numbers_only: v }))}
                    data-testid="whatsapp-authorized-toggle"
                  />
                </div>
                <Button onClick={handleSaveSettings} className="bg-green-600 hover:bg-green-700" data-testid="whatsapp-save-settings-btn">
                  Einstellungen speichern
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* === STATISTIKEN TAB === */}
        <TabsContent value="stats">
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            {[
              { label: 'Gesamt', value: stats?.total_messages || 0, color: 'bg-slate-100 text-slate-700' },
              { label: 'Eingehend', value: stats?.incoming || 0, color: 'bg-blue-100 text-blue-700' },
              { label: 'Ausgehend', value: stats?.outgoing || 0, color: 'bg-green-100 text-green-700' },
              { label: 'KI-Antworten', value: stats?.ai_replies || 0, color: 'bg-purple-100 text-purple-700' },
              { label: 'Ungelesen', value: stats?.unread || 0, color: 'bg-amber-100 text-amber-700' },
              { label: 'Kontakte', value: stats?.unique_contacts || 0, color: 'bg-teal-100 text-teal-700' },
            ].map(s => (
              <Card key={s.label}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="mt-4">
            <CardContent className="p-6">
              <Button variant="outline" onClick={() => { fetchStats(); fetchConversations(); }} data-testid="whatsapp-refresh-stats-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Statistiken aktualisieren
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
